import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  #accountStyleGeneration = 0;
  #accountStyleObserver = new MutationObserver(() => this.#styleAccountLogin());

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.addEventListener('open', this.#styleAccountLogin, true);
    this.#prepareAccountLogin();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#prepareAccountLogin();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.removeEventListener('open', this.#styleAccountLogin, true);
    this.#accountStyleGeneration++;
    this.#accountStyleObserver.disconnect();
  }

  #prepareAccountLogin() {
    const generation = ++this.#accountStyleGeneration;
    this.#accountStyleObserver.disconnect();
    if (!this.querySelector('shopify-account')) return;

    for (const name of ['shopify-account', 'shopify-login-form']) {
      customElements.whenDefined(name).then(() => {
        if (this.isConnected && generation === this.#accountStyleGeneration) {
          this.#styleAccountLogin();
        }
      });
    }
  }

  /**
   * Hide the Shop sign-in presentation only; retain Shopify's email form and auth.
   * The login form is lazy-rendered inside open shadow roots. These selectors
   * follow its current markup and must be rechecked after Shopify UI updates.
   */
  #styleAccountLogin = () => {
    this.#accountStyleObserver.disconnect();
    const accountRoot = this.querySelector('shopify-account')?.shadowRoot;
    if (!accountRoot || !this.isConnected) return;

    const loginRoot = accountRoot.querySelector('shopify-login-form')?.shadowRoot;
    if (loginRoot && !loginRoot.querySelector('style[data-bp-email-login]')) {
      const style = document.createElement('style');
      style.dataset.bpEmailLogin = '';
      style.textContent = `
        slot[name="shop-login"],
        .login-form__divider {
          display: none !important;
        }
      `;
      loginRoot.append(style);
    }

    this.#accountStyleObserver.observe(accountRoot, { childList: true, subtree: true });
    if (loginRoot) {
      this.#accountStyleObserver.observe(loginRoot, { childList: true, subtree: true });
    }
  };

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
